import { useEffect } from 'react';
import InnerPageHero from '../components/InnerPageHero';

const sections = [
  {
    title: 'Acceptance of Terms',
    content: [
      'By accessing or using the Best Neighbor Home Services website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.',
      'We reserve the right to modify these terms at any time. Continued use of our services after any changes constitutes your acceptance of the revised terms.',
    ],
  },
  {
    title: 'Services',
    content: [
      'Best Neighbor Home Services provides residential and commercial HVAC services throughout North Texas, including installation, repair, maintenance, and emergency services.',
      'All services are subject to availability and scheduling. We reserve the right to refuse service at our discretion. Service estimates are provided for informational purposes and final pricing may vary based on the actual scope of work required.',
    ],
  },
  {
    title: 'SMS / Text Message Communications',
    content: [
      'Opt-In Consent: By providing your mobile phone number and checking the SMS consent box on any of our website forms, you expressly consent to receive recurring automated and manually dialed text messages (SMS/MMS) from Best Neighbor Home Services at the mobile number provided. Consent to receive SMS messages is not a condition of purchasing any product or service from us.',
      'Message Types: Messages may include appointment reminders, service confirmations, estimates, maintenance alerts, promotional offers, and general communications related to your HVAC and home service needs.',
      'Message Frequency: Message frequency varies based on your service interactions and preferences. You may receive up to several messages per month.',
      'Message & Data Rates: Message and data rates may apply. Please contact your mobile carrier for details regarding your plan.',
      'How to Opt Out (STOP): You may cancel SMS communications at any time by replying STOP to any text message you receive from us. After texting STOP, you will receive one final confirmation message and no further messages will be sent. To re-enable messages, you may opt back in at any time through our website forms or by contacting us directly.',
      'How to Get Help (HELP): For assistance with our SMS program, reply HELP to any of our messages or contact us at:',
      '• Phone: (903) 321-2265\n• Email: help@bestneighborhomeservices.com',
      'No Third-Party Sharing: Your mobile phone number and SMS opt-in consent will not be sold, shared, leased, or transferred to any third party or affiliate for their own marketing or promotional purposes.',
      'Supported Carriers: Major U.S. carriers are supported, including but not limited to AT&T, T-Mobile, Verizon, and Sprint. Carrier support may vary.',
    ],
  },
  {
    title: 'Membership Plans',
    content: [
      'Membership plans are offered on a monthly or annual subscription basis. By enrolling in a membership plan, you agree to the recurring payment terms associated with your selected plan.',
      'Membership benefits are subject to the specific terms of your chosen plan and may be modified with reasonable notice. Cancellation policies and procedures will be provided at the time of enrollment.',
      'Sign-up bonuses and credits are applied as described in the plan details and may have specific conditions for use. Credits are non-transferable and have no cash value.',
    ],
  },
  {
    title: 'Scheduling and Appointments',
    content: [
      'Appointments are scheduled based on availability. While we strive to accommodate your preferred time, we cannot guarantee specific appointment times.',
      'If you need to cancel or reschedule an appointment, please provide at least 24 hours notice. Repeated no-shows or last-minute cancellations may result in service fees.',
    ],
  },
  {
    title: 'Pricing and Payment',
    content: [
      'All prices are quoted in US dollars. Payment is due at the time of service unless other arrangements have been made in advance.',
      'We accept major credit cards, checks, and financing options through our approved partners. Financing is subject to credit approval and the terms of the financing provider.',
      'Estimates provided before service are based on our assessment at the time and may be adjusted if additional work is discovered during the service process. Any changes will be communicated to you before proceeding.',
    ],
  },
  {
    title: 'Warranties and Guarantees',
    content: [
      'Best Neighbor Home Services offers warranties on parts and labor as specified at the time of service. Warranty terms vary by service type and are provided in writing upon completion of work.',
      'Warranties do not cover damage caused by misuse, neglect, natural disasters, or unauthorized modifications to installed equipment.',
      'Manufacturer warranties on equipment are separate from our service warranties and are governed by the manufacturer\'s terms and conditions.',
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      'Best Neighbor Home Services shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services or website.',
      'Our total liability for any claim arising from our services shall not exceed the amount paid for the specific service giving rise to the claim.',
      'We are not responsible for pre-existing conditions discovered during service, nor for damage to systems or property that results from conditions beyond our control.',
      'With respect to SMS communications, Best Neighbor Home Services is not liable for any delays or failures in the delivery of text messages, as delivery is subject to effective transmission by your mobile carrier.',
    ],
  },
  {
    title: 'Website Use',
    content: [
      'You may use our website for lawful purposes only. You agree not to use the website in any way that could damage, disable, or impair the site or interfere with any other party\'s use.',
      'All content on this website, including text, graphics, logos, and images, is the property of Best Neighbor Home Services and is protected by applicable copyright and trademark laws.',
      'You may not reproduce, distribute, or create derivative works from any content on this website without our prior written consent.',
    ],
  },
  {
    title: 'Indemnification',
    content: [
      'You agree to indemnify and hold harmless Best Neighbor Home Services, its officers, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or violation of these terms.',
    ],
  },
  {
    title: 'Governing Law',
    content: [
      'These Terms of Service are governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.',
      'Any disputes arising from these terms or our services shall be resolved in the courts of Collin County, Texas.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'If you have any questions about these Terms of Service or our SMS communications program, please contact us at:',
      'Best Neighbor Home Services\n414 Metro Park Dr Suite 201\nMcKinney, TX 75071\nPhone: (903) 321-2265\nEmail: help@bestneighborhomeservices.com',
    ],
  },
];

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="main-content" tabIndex={-1}>
      <InnerPageHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Terms of Service' },
        ]}
        title="Terms of"
        titleAccent="Service"
        description="Please read these terms carefully before using our website or services. These terms govern your use of Best Neighbor Home Services."
      />

      <section className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-brand-warm-gray mb-10">
            Last Updated: October 7, 2026
          </p>

          <div className="space-y-10">
            {sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy mb-4">
                  {index + 1}. {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-brand-warm-gray leading-relaxed text-[15px] whitespace-pre-line"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
